// Atoms — small reusable presentational components.

import React, { type CSSProperties, type ReactNode, useState } from "react";
import { createPortal } from "react-dom";

function _agoShort(tsMs: number): string {
  const secs = Math.floor((Date.now() - tsMs) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}
import type { AccountProvider, ContextPressure, SlotStatus } from "./types";
import { ctxArithmetic, fmtAgo } from "./utils";

// ─── icons (small inline svg set) ────────────────────────────────────────

type IconProps = React.SVGProps<SVGSVGElement>;

export const Icon = {
  Chev: (p: IconProps) => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" {...p}>
      <path
        d="M3.5 1.5 L6.5 5 L3.5 8.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Check: (p: IconProps) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}>
      <path
        d="M2.5 6.5 L5 9 L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  X: (p: IconProps) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" {...p}>
      <path d="M2 2 L9 9 M9 2 L2 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Camera: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4.5 3 L5.5 1.8 H8.5 L9.5 3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
  Send: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <path
        d="M1.5 6.5 L11.5 1.5 L6.5 11.5 L5.5 7.5 Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Refresh: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <path
        d="M11 6.5 a4.5 4.5 0 1 1 -1.4 -3.2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M11 1.5 V3.7 H8.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
    </svg>
  ),
  Warn: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M7 1.5 L13 12 H1 Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M7 5.5 V8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="7" cy="10" r="0.7" fill="currentColor" />
    </svg>
  ),
  Heart: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path
        d="M7 12 C2 8.5 1.5 5 3.6 3.5 C5.2 2.4 6.4 3.4 7 4.4 C7.6 3.4 8.8 2.4 10.4 3.5 C12.5 5 12 8.5 7 12 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
    </svg>
  ),
  Bolt: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path
        d="M8 1 L3 8 H6.5 L5.5 13 L10.5 6 H7 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
  Pause: (p: IconProps) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" {...p}>
      <rect x="2.5" y="2" width="2" height="7" fill="currentColor" />
      <rect x="6.5" y="2" width="2" height="7" fill="currentColor" />
    </svg>
  ),
  Play: (p: IconProps) => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" {...p}>
      <path d="M3 2 L9 5.5 L3 9 Z" fill="currentColor" />
    </svg>
  ),
  Gear: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <circle cx="7" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path
        d="M7 1.5 V3 M7 11 V12.5 M1.5 7 H3 M11 7 H12.5 M3.1 3.1 L4.2 4.2 M9.8 9.8 L10.9 10.9 M3.1 10.9 L4.2 9.8 M9.8 4.2 L10.9 3.1"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  SignOut: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <path
        d="M6.5 1.5 H2.5 V11.5 H6.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M8 4 L11 6.5 L8 9"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M5.5 6.5 H11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
    </svg>
  ),
  Lock: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" {...p}>
      <rect x="2.5" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.3" fill="none" />
      <path d="M4.5 6 V4 a2 2 0 0 1 4 0 V6" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" />
    </svg>
  ),
  Eye: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path
        d="M1 7 C2.5 4 4.6 2.5 7 2.5 S11.5 4 13 7 C11.5 10 9.4 11.5 7 11.5 S2.5 10 1 7 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
      <circle cx="7" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  ),
  // Speech-bubble outline — worker-tmux chat's entry point (distinct from Send,
  // which is the async one-way /message icon already in use elsewhere).
  Chat: (p: IconProps) => (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" {...p}>
      <rect x="1.5" y="2" width="11" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.1" fill="none" />
      <path d="M4.5 9.5 L4.5 12 L7.5 9.5" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" fill="none" />
    </svg>
  ),
  EyeOff: (p: IconProps) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M2 4 C4.5 7 9.5 7 12 4" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      <path
        d="M3 5 L1.6 6.6 M11 5 L12.4 6.6 M5.5 6 L4.7 8 M8.5 6 L9.3 8"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  ),
  // Stylised brain shape — two hemispheres + central fold. `filled` swaps
  // stroke-only for a filled silhouette, used to indicate "thinking on".
  Brain: (p: IconProps & { filled?: boolean }) => {
    const { filled, ...rest } = p;
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" {...rest}>
        <path
          d="M5 3.4 a2.2 2.2 0 0 0 -2.2 2.2 a1.8 1.8 0 0 0 -.6 3 a2.0 2.0 0 0 0 .4 2.7 a2.2 2.2 0 0 0 2.4 .6 V3.4 Z M11 3.4 a2.2 2.2 0 0 1 2.2 2.2 a1.8 1.8 0 0 1 .6 3 a2.0 2.0 0 0 1 -.4 2.7 a2.2 2.2 0 0 1 -2.4 .6 V3.4 Z"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinejoin="round"
          fill={filled ? "currentColor" : "none"}
        />
        <path
          d="M8 3 V13 M5.5 6.5 H 7 M8.8 6.5 H10.4 M5.7 9.5 H7 M9 9.5 H10.4"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          fill="none"
          opacity={filled ? 0.55 : 1}
        />
      </svg>
    );
  },
};

// ─── brand mark ──────────────────────────────────────────────────────────

export function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden>
      <svg width="13" height="13" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="5" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none" />
        <circle cx="6.5" cy="1.7" r="1.4" fill="white" />
        <circle cx="11.0" cy="9.0" r="1.1" fill="rgba(255,255,255,0.85)" />
        <circle cx="2.2" cy="9.0" r="1.1" fill="rgba(255,255,255,0.7)" />
      </svg>
    </span>
  );
}

// ─── pill select (backend switcher) ──────────────────────────────────────

export interface PillOption {
  id: string;
  label: string;
  url: string;
  status?: "ok" | "stale" | "unknown";
  last_pinged_at?: number | null;
}

interface PillSelectProps {
  value: string;
  options: PillOption[];
  onChange: (id: string) => void;
  formatLabel?: (o: PillOption) => ReactNode;
  formatRow?: (o: PillOption) => ReactNode;
}

export function PillSelect({ value, options, onChange, formatLabel, formatRow }: PillSelectProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", click);
    return () => window.removeEventListener("mousedown", click);
  }, [open]);

  const current = options.find((o) => o.id === value) ?? options[0];
  if (!current) return null;

  return (
    <div className="pill-select" ref={ref}>
      <button className="pill-select-trigger" onClick={() => setOpen((o) => !o)}>
        {formatLabel ? (
          formatLabel(current)
        ) : (
          <>
            <span className={`status-dot ${current.status === "ok" ? "working" : "stale"}`} />
            <span className="label">{current.label}</span>
            <span className="url">{current.url.replace(/^https?:\/\//, "")}</span>
          </>
        )}
        <span className="chev">
          <Icon.Chev style={{ transform: open ? "rotate(90deg)" : "rotate(0)", transition: "120ms" }} />
        </span>
      </button>
      {open && (
        <div className="pill-select-menu" role="listbox">
          {options.map((o) => (
            <div
              key={o.id}
              className={`pill-select-item ${o.id === value ? "active" : ""}`}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
              role="option"
              aria-selected={o.id === value}
            >
              <span className="check">
                <Icon.Check />
              </span>
              {formatRow ? (
                formatRow(o)
              ) : (
                <>
                  <span className={`status-dot ${o.status === "ok" ? "working" : "stale"}`} />
                  <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span>{o.label}</span>
                    <span className="meta">{o.url}</span>
                    {o.status !== "ok" && (
                      <span className="meta" style={{ color: "var(--status-stale, #b91c1c)", fontSize: 11 }}>
                        {o.last_pinged_at ? `offline · last seen ${_agoShort(o.last_pinged_at)}` : "offline"}
                      </span>
                    )}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── status atoms ────────────────────────────────────────────────────────

export function StatusDot({ status, className = "" }: { status: SlotStatus; className?: string }) {
  return <span className={`status-dot ${status} ${className}`} />;
}

/** Pure "why is this slot in this state" builder (ao_fleet_slot_state_reason_tooltips
 * 2026-08-27), factored out of StatusBadge so it's unit-testable — this dashboard's
 * vitest harness is jsdom-free / pure-function-only, same rationale as
 * tokenUsageBadgeTitle above. Reuses the plain-`title` tooltip convention already
 * shipped for ScheduledJobRunView's `h.detail`/`agent_exit_reason` badges rather than
 * inventing a new UI pattern. Falls back to the pre-existing "status: X" phase tooltip
 * when no reason field applies, so the one case that already worked keeps working
 * unchanged. */
export function statusBadgeReason({
  status,
  phase,
  idleReason,
  prebootElapsedSeconds,
  currentTask,
  staleReason,
  killReason,
  deathClass,
}: {
  status: SlotStatus;
  phase?: string | null;
  /** SlotView.idle_reason — why an idle slot is idle (reserve / declared wait). */
  idleReason?: string | null;
  /** SlotView.preboot_elapsed_seconds — how long this slot has been pre-booting. */
  prebootElapsedSeconds?: number | null;
  /** SlotView.current_task — paired with prebootElapsedSeconds for "about to work on X". */
  currentTask?: string | null;
  /** SlotView.stale_reason — silence/threshold + last-known context from the stale sweep. */
  staleReason?: string | null;
  /** SlotView.last_kill_reason — this slot's most recent kill's recorded reason string. */
  killReason?: string | null;
  /** SlotView.last_death_class — intentional_teardown / account_overage_exhausted / unexplained. */
  deathClass?: string | null;
}): string | undefined {
  if ((phase === "pre_boot" || phase === "booting") && prebootElapsedSeconds != null) {
    const mins = Math.floor(prebootElapsedSeconds / 60);
    const secs = prebootElapsedSeconds % 60;
    const dur = mins > 0 ? `${mins}m${secs}s` : `${secs}s`;
    return currentTask
      ? `pre-booting ${dur} — about to work on ${currentTask}`
      : `pre-booting ${dur} — no task pre-bound yet`;
  }
  if (status === "idle" && idleReason) return idleReason;
  if (status === "stale" && staleReason) return staleReason;
  if (status === "killed" && (killReason || deathClass)) {
    return [deathClass, killReason].filter(Boolean).join(" — ");
  }
  const label = phase && phase !== status ? phase : status;
  return label !== status ? `status: ${status}` : undefined;
}

export function StatusBadge({
  status,
  phase,
  idleReason,
  prebootElapsedSeconds,
  currentTask,
  staleReason,
  killReason,
  deathClass,
}: {
  status: SlotStatus;
  phase?: string | null;
  idleReason?: string | null;
  prebootElapsedSeconds?: number | null;
  currentTask?: string | null;
  staleReason?: string | null;
  killReason?: string | null;
  deathClass?: string | null;
}) {
  // Lifecycle phase (ao_worker_lifecycle 2026-07-10 Phase D): when the backend
  // computes a finer-grained phase (pre_boot / booting) than the stored status,
  // show the phase — that's what the operator asked the STATUS column to mean.
  const label = phase && phase !== status ? phase : status;
  const reason = statusBadgeReason({
    status,
    phase,
    idleReason,
    prebootElapsedSeconds,
    currentTask,
    staleReason,
    killReason,
    deathClass,
  });
  return (
    <span className="status-cell">
      <StatusDot status={status} />
      <span className={`status-label ${status}`} title={reason}>
        {label}
      </span>
    </span>
  );
}

// ─── provider badge ──────────────────────────────────────────────────────

/** Every registered `AccountProvider` value (`server/accounts.py`), mapped to a
 * short display label + a longer title tooltip. CORRECTED 2026-08-16: this map
 * used to not exist — `ProviderBadge` below hardcoded a DeepSeek-vs-everything-
 * else-is-"Claude" binary from the original 2-provider era
 * (deepseek_claude_blended_provider_routing_2026_07_28), which silently
 * mislabeled every Grok/Gemini/GLM/Codex/NVIDIA account as "Claude" in the
 * UI — exactly the "never register as Claude when it's Grok" failure mode the
 * operator explicitly flagged (2026-08-16). Also used by `AccountsPanel`'s
 * provider-grouping headers so the two stay in sync by construction, not by
 * convention. */
export const PROVIDER_DISPLAY: Record<string, { label: string; title: string; cssClass: string }> = {
  anthropic: { label: "Claude", title: "Routed to Claude (Anthropic)", cssClass: "anthropic" },
  // Title text preserved EXACTLY (not genericized) -- provider-badge.spec.ts asserts
  // this literal string.
  deepseek: { label: "DeepSeek", title: "Routed to DeepSeek V4 Pro", cssClass: "deepseek" },
  gemini: { label: "Gemini", title: "Routed to Google Gemini", cssClass: "gemini" },
  // Zhipu's product now goes to market as "Z.ai" (operator naming-alignment
  // ruling, model_provider_badge_mismatch_2026_08_21 follow-up) -- internal
  // AccountProvider id stays "glm" (dispatch/routing/pollers key off it), only
  // this display label changed.
  glm: { label: "Z.ai", title: "Routed to Zhipu GLM (Z.ai)", cssClass: "glm" },
  codex: { label: "Codex", title: "Routed to OpenAI Codex/Luna", cssClass: "codex" },
  nvidia: { label: "NVIDIA (Gemma)", title: "Routed to Gemma via NVIDIA NIM", cssClass: "nvidia" },
  ollama: { label: "Self-hosted", title: "Routed to Gemma via self-hosted Ollama", cssClass: "ollama" },
  groq: { label: "Groq", title: "Routed to Groq", cssClass: "other" },
  sambanova: { label: "SambaNova", title: "Routed to SambaNova", cssClass: "other" },
  // muse_spark_provider_addition_2026_08_25: contributor tier only.
  muse: { label: "Muse", title: "Routed to Meta Muse Spark (contributor tier)", cssClass: "muse" },
};

/** Which model provider a slot/account is running on, at a glance
 * (deepseek_claude_blended_provider_routing_2026_07_28 [UI] P1, corrected
 * 2026-08-16 to cover every registered provider — see `PROVIDER_DISPLAY`). null
 * (no account bound, or account_id no longer in accounts.json) renders nothing —
 * same "blank when unknown" convention as the operator/model cells around it. An
 * unrecognized provider string (a registered Literal value this map hasn't been
 * updated for yet) falls back to its raw name rather than lying as "Claude". */
export function ProviderBadge({ provider }: { provider: AccountProvider | null }) {
  if (!provider) return null;
  const entry = PROVIDER_DISPLAY[provider];
  return (
    <span className={`provider-badge ${entry?.cssClass ?? "other"}`} title={entry?.title ?? `Routed to ${provider}`}>
      {entry?.label ?? provider}
    </span>
  );
}

/** Compact in/out token count — provider-agnostic (deepseek_usage.py generalized,
 * 2026-08-05 follow-up: "tokens per task and by orchestrator" ruling). Renders
 * nothing when both are null (no matching session / no poller tick yet), same
 * blank-when-unknown convention as ProviderBadge right beside it.
 *
 * Cache-token split (ao_fleet_cache_tokens_and_task_count_2026_08_05) — an
 * optional ⚡{cache read} segment appears only when there's real cache activity,
 * so a session that never hits its cache reads exactly like before this change.
 * The write/read breakdown always lands in the tooltip, matching the done-task
 * drill-down's Input/Cache write/Cache read/Output convention (BacklogDetailModal)
 * without needing 4 columns' worth of space in an inline badge. */
/** Pure title-string builder for TokenUsageBadge — split out so its branching
 * (cache segment / reasoning segment / plain) is unit-testable under this
 * dashboard's jsdom-free, PURE-function-only vitest harness (vitest.config.ts:
 * `environment: "node"`, no DOM/React render) rather than requiring a
 * react-testing-library render this repo deliberately doesn't set up. */
export function tokenUsageBadgeTitle({
  baseTitle,
  cacheCreationTokens,
  cacheReadTokens,
  reasoningTokens,
}: {
  baseTitle: string;
  cacheCreationTokens?: number | null;
  cacheReadTokens?: number | null;
  reasoningTokens?: number | null;
}): string {
  const hasCache = (cacheCreationTokens ?? 0) > 0 || (cacheReadTokens ?? 0) > 0;
  let fullTitle = hasCache
    ? `${baseTitle} — cache write ${fmtTokenCount(cacheCreationTokens ?? 0)}, cache read ${fmtTokenCount(cacheReadTokens ?? 0)}`
    : baseTitle;
  if (reasoningTokens != null) {
    fullTitle += `${hasCache ? "," : " —"} reasoning ${fmtTokenCount(reasoningTokens)} (within output, not additive spend)`;
  }
  return fullTitle;
}

export function TokenUsageBadge({
  inputTokens,
  outputTokens,
  cacheCreationTokens,
  cacheReadTokens,
  spendUsd,
  reasoningTokens,
  turnCount,
  title,
}: {
  inputTokens: number | null;
  outputTokens: number | null;
  cacheCreationTokens?: number | null;
  cacheReadTokens?: number | null;
  spendUsd?: number | null;
  /** DeepSeek native-proxy reasoning-token breakdown (deepseek_native_usage_
   * reasoning_tokens_visibility_2026_08_12) — a subset WITHIN outputTokens, NOT
   * additive spend (DeepSeek's rate card has no separate reasoning tier). Rendered
   * as its own inline segment ONLY when non-null, so a session with no matching
   * DeepSeekNativeUsageRow (non-DeepSeek, or predates the native proxy) renders
   * exactly as before this prop existed — never clutters the compact badge with a
   * misleading 0. */
  reasoningTokens?: number | null;
  /** Turn count (ao_v2_fleet_visibility_and_reconciliation_2026_09_03 Phase 5
   * BACKEND/UI P2, ~189/~197) — SlotView.session_turn_count, today only ever
   * populated for a node-hosted slot (forwarded from WorkerSlotObservation; central
   * tracks no live turn count at all). Rendered as its own segment ONLY when
   * non-null, same blank-when-unknown convention as reasoningTokens above — never a
   * fabricated 0 standing in for "not measured". */
  turnCount?: number | null;
  title?: string;
}) {
  if (inputTokens == null && outputTokens == null) return null;
  const hasCache = (cacheCreationTokens ?? 0) > 0 || (cacheReadTokens ?? 0) > 0;
  const fullTitle = tokenUsageBadgeTitle({
    baseTitle: title ?? "Tokens for the current session",
    cacheCreationTokens,
    cacheReadTokens,
    reasoningTokens,
  });
  return (
    <span className="token-usage-badge mono" title={fullTitle}>
      ↓{fmtTokenCount(inputTokens ?? 0)} ↑{fmtTokenCount(outputTokens ?? 0)}
      {hasCache && <> ⚡{fmtTokenCount(cacheReadTokens ?? 0)}</>}
      {/* DeepSeek native reasoning-token breakdown — a distinct visual segment (not
          folded into ↑) so it reads as "informational subset", never as additive
          spend. Omitted entirely (not "🧠0") when null, same blank-when-unknown
          convention as every other optional segment in this badge. */}
      {reasoningTokens != null && <> 🧠{fmtTokenCount(reasoningTokens)}</>}
      {/* Turn count — omitted entirely (not " · 0 turns") when null. */}
      {turnCount != null && (
        <>
          {" "}
          · {turnCount} turn{turnCount === 1 ? "" : "s"}
        </>
      )}
      {/* ao_fleet_live_spend_badge_2026_08_05 — priced via the same price_usage()
          engine the Task Token Usage / Accounts balance panels are built from
          (server/deepseek_usage.py), just surfaced live per-slot. Omitted (not
          "$—") when null: unpriced provider, or no poller tick yet — same
          blank-when-unknown convention as the rest of this badge. */}
      {spendUsd != null && <> {fmtUsd(spendUsd)}</>}
    </span>
  );
}

export function fmtTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** "$0.0271" — same precision as TaskUsageWindows.tsx's fmtUsdOrDash (kept as a
 * separate local formatter since that module imports FROM this one, not the
 * other way around). Never called for null; TokenUsageBadge omits the segment
 * entirely instead of rendering a "$—" placeholder. */
export function fmtUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}

// ─── context bar ─────────────────────────────────────────────────────────

interface CtxBarProps {
  pct: number;
  pressure: ContextPressure;
  compactionsLastHour?: number;
  /** Reading is a frozen snapshot (no recent heartbeat) — show it as last-known. */
  stale?: boolean;
  /** Live token count behind `pct`. Null/undefined when the probe had no reading. */
  tokens?: number | null;
  /** Window `pct` was divided by. See ctxArithmetic — this is the number worth eyeballing. */
  windowTokens?: number | null;
}

export function CtxBar({ pct, pressure, compactionsLastHour, stale, tokens, windowTokens }: CtxBarProps) {
  const arithmetic = ctxArithmetic(tokens, windowTokens);
  const head =
    compactionsLastHour != null
      ? `${pct}% context (${pressure}) · ${compactionsLastHour} compaction${compactionsLastHour === 1 ? "" : "s"}/hr`
      : `${pct}% context used (${pressure})`;
  const base = arithmetic ? `${head}\n${arithmetic}` : head;
  // A frozen reading must not be presented as current: both fields only advance on a
  // heartbeat carrying context_used_pct, so a silent/wedged worker keeps asserting its
  // last value (prod slot 3: `100% / thrashing`, zero heartbeats).
  const title = stale ? `${base} — STALE: last known, no recent heartbeat` : base;
  return (
    <div className={`ctx-cell${stale ? " ctx-stale" : ""}`}>
      <div className="ctx-bar" title={title}>
        <div className={`ctx-fill ${pressure}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className={`ctx-num ${pressure}`}>
        {pct}%
        {stale ? (
          <span className="ctx-stale-mark" title={title}>
            ?
          </span>
        ) : null}
      </span>
      {compactionsLastHour != null && compactionsLastHour > 0 && (
        <span
          className={`mono ${pressure === "thrashing" ? "thrashing-tag" : ""}`}
          style={{ fontSize: 10.5, color: pressure === "thrashing" ? "var(--status-stale)" : "var(--fg-2)" }}
          title={`${compactionsLastHour} context compactions in the last hour`}
        >
          ⟳{compactionsLastHour}/hr
        </span>
      )}
    </div>
  );
}

// ─── toggle ──────────────────────────────────────────────────────────────

export function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button className={`toggle ${on ? "on" : ""}`} onClick={() => onChange(!on)} role="switch" aria-checked={on}>
      <span className="knob" />
    </button>
  );
}

// ─── live pill (top bar) ─────────────────────────────────────────────────

interface LivePillProps {
  connected: boolean;
  lastUpdated: string;
  serverStarted?: string | null;
  pollIntervalLabel?: string;
}

export function LivePill({ connected, lastUpdated, serverStarted, pollIntervalLabel = "60s" }: LivePillProps) {
  const title = !connected
    ? "disconnected"
    : serverStarted
      ? `polling /api/state every ${pollIntervalLabel} · server started ${new Date(serverStarted).toLocaleString()}`
      : `polling /api/state every ${pollIntervalLabel}`;
  return (
    <span className={`livepill ${connected ? "" : "off"}`} title={title}>
      <span className="dot" />
      <span>{connected ? "live" : "off"}</span>
      <span style={{ color: "var(--fg-3)" }}>·</span>
      <span style={{ color: "var(--fg-2)" }}>{lastUpdated}</span>
      {/* Visible restart marker, not just the hover title (operator ask, 2026-08-13):
       * an orchestrator SERVICE restart (e.g. ao-self-pull.sh picking up a new LDR
       * commit) kills every slot's tmux session fleet-wide, same blast radius as a
       * tmux-server-death bug but a different, expected mechanism — needs to be
       * visible at a glance so the two aren't confused for one another. */}
      {connected && serverStarted && (
        <>
          <span style={{ color: "var(--fg-3)" }}>·</span>
          <span style={{ color: "var(--fg-2)" }}>restarted {fmtAgo(serverStarted)}</span>
        </>
      )}
    </span>
  );
}

// ─── tabs (small segmented control) ──────────────────────────────────────

interface TabsProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}

export function Tabs<T extends string>({ value, onChange, options }: TabsProps<T>) {
  return (
    <div className="tabs" role="tablist">
      {options.map((o) => (
        <button key={o.value} className={`tab ${o.value === value ? "active" : ""}`} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── panel shell ─────────────────────────────────────────────────────────

interface PanelProps {
  title: string;
  count?: number | string;
  right?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  /** Role-based tone for the panel header. Each role resolves to a
   * CSS var (--tone-<role>-color) that the active palette
   * (body[data-palette]) restyles. So a panel says "I am agents" — not
   * "I am yellow" — and changing palette swaps colors site-wide. */
  tone?: "agents" | "fleet" | "backlog" | "blocked" | "prerequisites" | "activity" | "accounts" | "resources";
  /** When true, the title becomes a toggle that shows/hides the panel body
   * (2026-08-16, operator request — Wallet Reconciliation/Accounts/Fleet/
   * Blocked/Escalations/Scheduled-task panels). Collapse state persists per
   * panel (keyed on `title`) via localStorage, matching this dashboard's
   * existing session/tweaks persistence pattern in App.tsx. Only the
   * title+chevron is the click target, not the whole header — `right`'s own
   * buttons (e.g. Fleet's "Spawn worker") must stay independently clickable,
   * not swallowed by a header-wide toggle. */
  collapsible?: boolean;
}

const PANEL_COLLAPSE_KEY_PREFIX = "orch.panelCollapsed.";

function readPanelCollapsed(title: string): boolean {
  try {
    return localStorage.getItem(PANEL_COLLAPSE_KEY_PREFIX + title) === "1";
  } catch {
    // localStorage unavailable (private mode / quota) -- default to expanded.
    return false;
  }
}

function writePanelCollapsed(title: string, collapsed: boolean): void {
  try {
    localStorage.setItem(PANEL_COLLAPSE_KEY_PREFIX + title, collapsed ? "1" : "0");
  } catch {
    // Collapse still works for the current session, it just won't persist across reloads.
  }
}

export function Panel({ title, count, right, children, flush, tone, collapsible }: PanelProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => (collapsible ? readPanelCollapsed(title) : false));

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writePanelCollapsed(title, next);
      return next;
    });
  };

  return (
    <div className={`panel${tone ? ` tone-${tone}` : ""}`}>
      <div className="panel-head">
        {collapsible ? (
          <button
            type="button"
            className="panel-collapse-toggle"
            onClick={toggle}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand" : "Collapse"}
          >
            <span className="panel-collapse-chevron" aria-hidden="true">
              {collapsed ? "▸" : "▾"}
            </span>
            <span className="title">{title}</span>
          </button>
        ) : (
          <span className="title">{title}</span>
        )}
        {typeof count !== "undefined" && (
          <span className="count">
            · <b>{count}</b>
          </span>
        )}
        <span className="spacer" />
        {right}
      </div>
      {!collapsed && <div className={`panel-body ${flush ? "flush" : ""}`}>{children}</div>}
    </div>
  );
}

// ─── empty state ─────────────────────────────────────────────────────────

interface EmptyProps {
  icon?: ReactNode;
  title: string;
  sub?: ReactNode;
}

export function Empty({ icon, title, sub }: EmptyProps) {
  return (
    <div className="empty-block">
      {icon && <div className="e-mark">{icon}</div>}
      <div style={{ color: "var(--fg-1)", fontWeight: 500, marginBottom: 2 }}>{title}</div>
      {sub && <div>{sub}</div>}
    </div>
  );
}

// ─── modal ───────────────────────────────────────────────────────────────

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title: string;
  danger?: boolean;
  style?: CSSProperties;
}

export function Modal({ open, onClose, children, title, danger, style }: ModalProps) {
  if (!open) return null;
  // Portaled straight to document.body (usage_time_series_chart_2026_08_18 finding):
  // `.modal-back` is `position: fixed`, which is fixed relative to the VIEWPORT only
  // when no ancestor establishes its own containing block — but `.topbar` sets
  // `backdrop-filter` (for its own frosted-glass look), which DOES establish one per
  // the CSS spec (same trigger as `transform`/`filter`/`perspective`/`will-change`).
  // A Modal instantiated from inside a TopBar popover (e.g. FleetKpisMenu's "KPIs"
  // dropdown) rendered fixed-relative-to-the-144px-tall-topbar instead of the
  // viewport — visible content sat far outside actual viewport bounds, unclickable
  // and unscrollable-into-view, confirmed via getBoundingClientRect() while wiring
  // UsageTimeSeriesModal's FleetKpis entry point (this was Modal's first-ever
  // caller nested under `.topbar`; every existing Modal usage renders from
  // App.tsx's top level, never from inside a TopBar popover, so this pre-existing
  // bug never manifested before). A portal makes `.modal-back` a DOM child of
  // <body> regardless of the React tree it's called from, escaping any ancestor's
  // containing block — the fix generalizes to every future modal-in-popover case,
  // not just this one.
  return createPortal(
    <div
      className="modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" style={style}>
        <div className="modal-head">
          {danger && <span className="badge">destructive</span>}
          <span>{title}</span>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
